export const ROLES = [
  'admin',
  'recruiter',
  'viewer',
  'center_manager',
  'program_manager',
  'accountant',
  'employee',
] as const;
export type Role = (typeof ROLES)[number];

/** Офісні ролі: бачать реєстр, конкурси, персонал. `employee` — лише власний кабінет. */
export const OFFICE_ROLES = [
  'admin',
  'recruiter',
  'viewer',
  'center_manager',
  'program_manager',
  'accountant',
] as const satisfies readonly Role[];

/** HR-відділ: веде конкурси, кандидатів, реєстр посад, персонал і контракти. */
export const HR_ROLES = ['admin', 'recruiter'] as const satisfies readonly Role[];

export const CONTEST_STATUSES = ['draft', 'open', 'closed', 'cancelled'] as const;
export type ContestStatus = (typeof CONTEST_STATUSES)[number];

export const SOURCE_KINDS = ['job_board', 'email', 'referral', 'other'] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const READY_FOR_FOP = ['yes', 'no', 'unknown'] as const;
export type ReadyForFop = (typeof READY_FOR_FOP)[number];

export const DUPLICATE_REASONS = [
  'same_phone',
  'same_email',
  'same_resume',
  'similar_name',
] as const;
export type DuplicateReason = (typeof DUPLICATE_REASONS)[number];

export const DUPLICATE_STATUSES = ['open', 'merged', 'dismissed'] as const;
export type DuplicateStatus = (typeof DUPLICATE_STATUSES)[number];

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

/** Поріг `similarity()` pg_trgm для схожих ПІБ (правило 5.4). */
export const NAME_SIMILARITY_THRESHOLD = 0.6;

/** Ліміт розміру файлу резюме. */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

// ───────────── WSDMS: реєстр посад, персонал, облік часу, послуги ─────────────

export const PROJECT_STATUSES = ['planned', 'active', 'closed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** Тип контракту (модальність залучення). */
export const CONTRACT_KINDS = ['employment', 'civil', 'fop'] as const;
export type ContractKind = (typeof CONTRACT_KINDS)[number];

/** Одиниця ставки: місячна, погодинна, за одиницю послуги. */
export const RATE_UNITS = ['month', 'hour', 'service'] as const;
export type RateUnit = (typeof RATE_UNITS)[number];

/** Статус штатної одиниці, який ставиться вручну (зайнятість рахується з призначень). */
export const STAFF_POSITION_STATUSES = ['active', 'suspended', 'closed'] as const;
export type StaffPositionStatus = (typeof STAFF_POSITION_STATUSES)[number];

/** Підсумковий статус для показу (правило 12.4.1). */
export const OCCUPANCY_STATUSES = [
  'vacant',
  'partially_occupied',
  'occupied',
  'over_occupied',
  'suspended',
  'closed',
] as const;
export type OccupancyStatus = (typeof OCCUPANCY_STATUSES)[number];

export const SERVICE_UNITS = ['hour', 'session', 'consultation', 'visit', 'item'] as const;
export type ServiceUnit = (typeof SERVICE_UNITS)[number];

export const COMMITTEE_ROLES = ['chair', 'secretary', 'member'] as const;
export type CommitteeRole = (typeof COMMITTEE_ROLES)[number];

/** Документи кандидата (таблиця `resumes` зберігає всі типи). */
export const DOCUMENT_KINDS = [
  'resume',
  'motivation_letter',
  'diploma',
  'certificate',
  'recommendation',
  'other',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const EMPLOYEE_STATUSES = ['active', 'dismissed'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const CONTRACT_STATUSES = ['draft', 'active', 'ended', 'terminated'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Шаблони документів, які генерує система. */
export const TEMPLATE_KINDS = [
  'contract_employment',
  'contract_civil',
  'contract_fop',
  'amendment',
  'service_act',
] as const;
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];

export const DAY_KINDS = ['working', 'short', 'weekend', 'holiday'] as const;
export type DayKind = (typeof DAY_KINDS)[number];

/** Коди відсутностей у табелі. */
export const ABSENCE_CODES = ['vacation', 'sick', 'unpaid', 'business_trip', 'other'] as const;
export type AbsenceCode = (typeof ABSENCE_CODES)[number];

export const TIMESHEET_STATUSES = ['draft', 'submitted', 'approved', 'returned'] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

/** Звіт про надану послугу: погодження керівником центру → керівником програми → HR. */
export const REPORT_STATUSES = [
  'draft',
  'submitted',
  'center_approved',
  'program_approved',
  'approved',
  'returned',
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** Категорії об'єктів КАТОТТГ. */
export const KATOTTG_CATEGORIES = ['O', 'K', 'P', 'H', 'M', 'X', 'C', 'B'] as const;
export type KatottgCategory = (typeof KATOTTG_CATEGORIES)[number];

export const ACT_STATUSES = ['draft', 'issued', 'signed', 'cancelled'] as const;
export type ActStatus = (typeof ACT_STATUSES)[number];

export const REGISTER_STATUSES = ['draft', 'sent', 'paid'] as const;
export type RegisterStatus = (typeof REGISTER_STATUSES)[number];
